import React, { useEffect, useState } from "react";
import { useAppContext } from "../../share-components/AppContext";
import { useNavigate } from "react-router-dom";
import FormTitle from "../../share-components/FormTitle";
import SearchColumnSelect from "../../share-components/SearchColumnSelect";
import CustomTable from "../../share-components/CustomTable";
import {
    currentMonth, currentYear, customStorage, formatDateTime, getAvailableMonths, getChangingSelectColor, getPayloadData,
    handleFromMonthChange, handleFromYearChange, handleToMonthChange, handleToYearChange, isNullOrUndefinedObj, years
} from "../../services/UtilityService";
import { SERVER_ERROR_MESSAGE, TOKEN_SESSION_KEY } from "../../constants/commonConstants";
import { Button, Card } from "react-bootstrap";
import { getAllActiveUsers, getLastLockedYearMonth } from "../../services/CommonService";
import { PAGE_GENERAL_EXPEND_LIST, ROUTE_EDIT_GENERAL_EXPEND, ROUTE_GENERAL_EXPEND_DETAILS, ROUTE_GENERAL_EXPEND_LIST } from "../../constants/routeConstants";
import { getGeneralExpends } from "../../services/GeneralExpendService";
import NoDataFound from "../../share-components/NoDataFound";
import useScrollRestoration from "../../share-components/UseScrollRestoration";

const GeneralExpendList = () => {

    useScrollRestoration(PAGE_GENERAL_EXPEND_LIST);

    const { loading, setLoading, setShowApiErrorMessage, setRouteNaviData } = useAppContext();
    const [expendList, setExpendList] = useState(null);
    const [filteredData, setFilteredData] = useState([]);
    const [isFirstLoaded, setIsFirstLoaded] = useState(false);
    const navigate = useNavigate();

    const token = customStorage.getItem(TOKEN_SESSION_KEY);
    const currentUser = getPayloadData(token);

    const initialSearchDataObj = sessionStorage.getItem(ROUTE_GENERAL_EXPEND_LIST);
    const initialSearchData = initialSearchDataObj ? JSON.parse(initialSearchDataObj) : null;

    const [lastLockedYearMonth, setLastLockedYearMonth] = useState(null);
    const [fromYear, setFromYear] = useState(initialSearchData?.fromYear || currentYear);
    const [fromMonth, setFromMonth] = useState(initialSearchData?.fromMonth || currentMonth);
    const [toYear, setToYear] = useState(initialSearchData?.toYear || currentYear);
    const [toMonth, setToMonth] = useState(initialSearchData?.toMonth || currentMonth);
    const [expendedById, setExpendedById] = useState(initialSearchData?.expendedById || "");
    const [afterGetDataStore, setAfterGetDataStore] = useState({
        fromYear: initialSearchData?.fromYear || currentYear,
        fromMonth: initialSearchData?.fromMonth || currentMonth,
        toYear: initialSearchData?.toYear || currentYear,
        toMonth: initialSearchData?.toMonth || currentMonth,
        expendedById: initialSearchData?.expendedById || ""
    });

    const [activeUsers, setActiveUsers] = useState([]);
    const [searchData, setSearchData] = useState(null);

    useEffect(() => {
        const searchData = {
            fromYear,
            fromMonth,
            toYear,
            toMonth,
            expendedById
        };
        setSearchData(searchData);
    }, [loading]);

    const fetchActiveUsers = async () => {
        const response = await getAllActiveUsers();
        const sortedUsers = (response || [])?.sort((a, b) => (a.username || "")?.localeCompare((b?.username || "")));
        setActiveUsers(sortedUsers);
    }

    const fetchLastLockedDate = async () => {
        const lastLockedData = await getLastLockedYearMonth();
        setLastLockedYearMonth(lastLockedData || null);
    }


    const fetchExpendList = async () => {
        try {
            setLoading(true);
            const formData = {
                expendedById: expendedById || null,
                fromYear: fromYear,
                fromMonth: fromMonth,
                toYear: toYear,
                toMonth: toMonth
            }
            const response = await getGeneralExpends(formData);
            const expends = (response?.generalExpendList || [])?.map(data => ({
                ...data,
                formatedExpendDate: formatDateTime(data?.expendedDate?.slice(0, 8))
            }));
            setExpendList(expends);
            const afterFetchStore = {
                fromYear,
                fromMonth,
                toYear,
                toMonth,
                expendedById: expendedById
            }
            setAfterGetDataStore(afterFetchStore);
            setIsFirstLoaded(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchInitialLoad = async () => {
        try {
            setLoading(true);
            await fetchLastLockedDate();
            await fetchActiveUsers();
            await fetchExpendList();
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        setRouteNaviData("GENERAL EXPEND >> EXPEND LIST");
        fetchInitialLoad();
    }, []);

    const canValidProcess = (expendedDate, expendedById) => {
        const dateStr = expendedDate || '';
        const expendYear = dateStr.length >= 4 ? parseInt(dateStr.slice(0, 4), 10) : 0;
        const expendMonth = dateStr.length >= 6 ? parseInt(dateStr.slice(4, 6), 10) : 0;
        const isValidUser = expendedById === currentUser?.userId;
        const isValidLockDate = isNullOrUndefinedObj(lastLockedYearMonth) || lastLockedYearMonth.toYear < expendYear ||
            (lastLockedYearMonth.toYear === expendYear && lastLockedYearMonth.toMonth < expendMonth);
        return isValidUser && isValidLockDate;
    }

    const columns = [
        {
            Header: 'Reason',
            accessor: 'expendedReason',
            style: {
                maxWidth: "200px"
            }
        },
        {
            Header: 'Amount(¥)',
            accessor: 'expendedPrice',
            style: {
                maxWidth: "200px"
            }
        },
        {
            Header: 'Date',
            accessor: 'formatedExpendDate',
            style: {
                maxWidth: "200px",
                fontWeight: "bold"
            }
        },
        {
            Header: 'Expended By',
            accessor: 'expendedBy',
            style: {
                maxWidth: "200px"
            }
        },
        {
            Header: 'Edit',
            accessor: 'edit',
            unSearchable: true,
            disableSortBy: true,
            headerClassName: 'disableSort text-center',
            className: 'text-center align-middle',
            style: {
                minWidth: "70px",
                // maxWidth: "100px"
            },
            Cell: ({ row }) => (
                <div className='center-block'>
                    {canValidProcess(row.original?.expendedDate, row.original?.expendedById) ? <Button
                        variant="secondary"
                        style={{
                            padding: "0px 5px",
                            margin: "5px"
                        }}
                        onClick={() => navigate(`${ROUTE_EDIT_GENERAL_EXPEND}/${row.original?.generalExpendId}`)}
                    >
                        <i className="bi bi-pencil-square"></i>
                        {/* <span>Edit</span> */}
                    </Button> :
                        <Button variant="danger" disabled
                            style={{
                                padding: "0px 5px",
                                margin: "5px"
                            }}>
                            <i className="bi bi-ban"></i>
                            {/* <span>Disabled</span> */}
                        </Button>
                    }
                </div>
            ),
        },
        {
            Header: 'Details',
            accessor: 'details',
            unSearchable: true,
            disableSortBy: true,
            headerClassName: 'disableSort text-center',
            className: 'text-center align-middle',
            style: {
                minWidth: "50px",
                // maxWidth: "100px"
            },
            Cell: ({ row }) => (
                <div className='center-block'>
                    <Button
                        variant="secondary"
                        style={{
                            padding: "0px 5px",
                            margin: "5px"
                        }}
                        onClick={() => navigate(`${ROUTE_GENERAL_EXPEND_DETAILS}/${row.original?.generalExpendId}`)}
                    >
                        <i className="bi bi-info-circle"></i>
                        {/* <span>Details</span> */}
                    </Button>
                </div>
            ),
        },
    ];

    return (
        <div id='generalExpendListPage'>
            <FormTitle title={"Expend List"} isCenter={true} />
            {isFirstLoaded && <>
                <div style={{ textAlign: 'center', fontWeight: 'bold' }}>
                    {afterGetDataStore?.fromYear}/{afterGetDataStore?.fromMonth} ～ {afterGetDataStore?.toYear}/{afterGetDataStore?.toMonth}
                </div>
                <div style={{ overflow: "auto", paddingBottom: "50px" }}>
                    <div style={{ minWidth: "max-content", maxWidth: "850px", margin: '0 auto' }}>
                        <Card>
                            <Card.Body style={{
                                padding: '10px 5px 20px 5px'
                            }}>
                                <div>
                                    <div>
                                        <div className="mt-1 mb-1" style={{ display: "flex", gap: "10px", justifyContent: "start", alignItems: "end", flexWrap: "wrap" }}>
                                            {/* From Year & Month */}
                                            <div className="d-inline">
                                                <label className="me-1 custom-label" htmlFor="fromYear" style={{ minWidth: "50px" }}>
                                                    From
                                                </label>
                                                <div>
                                                    <select
                                                        id="fromYear"
                                                        className={`customInputControl ${getChangingSelectColor(fromYear, afterGetDataStore?.fromYear)}`}
                                                        value={fromYear}
                                                        onChange={(e) => handleFromYearChange(e, fromMonth, toYear, toMonth, setFromYear, setFromMonth, setToYear, setToMonth)}
                                                    >
                                                        {years.map((year) => (
                                                            <option key={year} value={year}>{year}</option>
                                                        ))}
                                                    </select>
                                                    <span className="separator">/</span>
                                                    <select
                                                        id="fromMonth"
                                                        className={`customInputControl ${getChangingSelectColor(fromMonth, afterGetDataStore?.fromMonth)}`}
                                                        value={fromMonth}
                                                        onChange={(e) => handleFromMonthChange(e, fromYear, toYear, toMonth, setFromMonth, setToMonth)}
                                                    >
                                                        {getAvailableMonths(fromYear).map((month) => (
                                                            <option key={month} value={month}>
                                                                {month.toString().padStart(2, "0")}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            {/* To Year & Month */}
                                            <div className="d-inline">
                                                <label className="me-1 custom-label" htmlFor="toYear" style={{ minWidth: "50px" }}>
                                                    To
                                                </label>
                                                <div>
                                                    <select
                                                        id="toYear"
                                                        className={`customInputControl ${getChangingSelectColor(toYear, afterGetDataStore?.toYear)}`}
                                                        value={toYear}
                                                        onChange={(e) => handleToYearChange(e, fromYear, fromMonth, toMonth, setFromYear, setToYear, setFromMonth)}
                                                    >
                                                        {years.map((year) => (
                                                            <option key={year} value={year}>{year}</option>
                                                        ))}
                                                    </select>
                                                    <span className="separator">/</span>
                                                    <select
                                                        id="toMonth"
                                                        className={`customInputControl ${getChangingSelectColor(toMonth, afterGetDataStore?.toMonth)}`}
                                                        value={toMonth}
                                                        onChange={(e) => handleToMonthChange(e, fromYear, fromMonth, toYear, setFromMonth, setToMonth)}
                                                    >
                                                        {getAvailableMonths(toYear).map((month) => (
                                                            <option key={month} value={month}>
                                                                {month.toString().padStart(2, "0")}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ marginBottom: '5px', marginTop: '10px', display: 'flex', justifyContent: 'start', alignItems: 'end', flexWrap: 'wrap', gap: '5px' }}>
                                            {/* Purchased By */}
                                            <div className="d-inline">
                                                <label className="me-1 custom-label" htmlFor="userBy" style={{ minWidth: "50px" }}>
                                                    Admin By
                                                </label>
                                                <div>
                                                    <select
                                                        id="userBy"
                                                        className={`customInputControl select-min-max-width ${getChangingSelectColor(expendedById, afterGetDataStore?.expendedById)}`}
                                                        value={expendedById}
                                                        onChange={(e) => setExpendedById(e.target.value || '')}
                                                    >
                                                        <option value="">--All--</option>
                                                        {activeUsers.map(user => (
                                                            <option key={user.userId} value={user.userId}>{user.username}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="d-inline">
                                            <button type='button' className="btn btn-success btn-sm appBtn"
                                                onClick={fetchExpendList}
                                            >Get Data</button>
                                        </div>
                                        <div style={{ width: "max-content" }}>
                                            <div style={{ marginBottom: '10px', marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'end', flexWrap: 'wrap', gap: '5px' }}>
                                                <div style={{ display: 'inline-block' }}>
                                                    <SearchColumnSelect data={expendList} setData={setFilteredData} columns={columns}
                                                        storeSessionKey={ROUTE_GENERAL_EXPEND_LIST}
                                                        sessionData={searchData} />
                                                </div>
                                            </div>
                                            {filteredData?.length > 0 ?
                                                <CustomTable
                                                    tableId='generalExpendListTbl'
                                                    columns={columns}
                                                    tblData={filteredData || []}
                                                    showPagination={true}
                                                    itemsPerPage={20}
                                                    initialSortBy={[{ id: 'formatedExpendDate', desc: true }]}
                                                    isStorePageData={true}
                                                /> : <NoDataFound />}
                                        </div>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                </div>
            </>}
        </div>
    );
}

export default GeneralExpendList;